import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageWrapper from '../../components/PageWrapper';
import { useAuthEffects } from '../../components/Auth';
import { Divider, Grid2, Box, Card, Typography, Chip, useTheme, useMediaQuery, ImageList, Snackbar, Alert } from '@mui/material';
import { usePlansListPublicQuery } from '../../services/plans';
import {PlanListItemContainer, PlanListItemEl} from '../../components/PlanList';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import SuggestionDialog from './components/SuggestionDialog';
import { useFindGlobalTagsQuery } from '../../services/tags';
import AddToWishlistDialog, { useWishlistLogic } from './components/WishlistDialog';
import ReactGA from 'react-ga4';
import arrowLeftImage from '../../assets/arrow_left.png';
import arrowRightImage from '../../assets/arrow_right.png';
import arrowLeftImageDark from '../../assets/arrow_left_dark.png';
import arrowRightImageDark from '../../assets/arrow_right_dark.png';
import { RegionSelector } from '../../components/RegionSelector';
import { useShippingRegionContext } from '../../contexts/ShippingRegionContext';
import { useFavourites } from '../../contexts/FavouritesContext';

export const AddPlanHintCard = ({ onCreate }: { onCreate: () => void }) => {
	const { t } = useTranslation();
	return (
		<Card sx={{ 
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			cursor: 'pointer',
			'&:hover': {
				bgcolor: 'action.hover',
			},
			borderRadius: '8px',
			height: 340,
		}} onClick={onCreate}>
			<Box sx={{ 
				display: 'flex', 
				flexDirection: 'column',
				alignItems: 'center',
				gap: 2,
				p: 4,
			}}>
				<AddIcon sx={{ fontSize: 48, color: 'primary.main' }} />
				<Typography variant="h6" color="primary" textAlign='center'>
					{t('discover.addPlanHintCard.title')}
				</Typography>
				<Typography variant="body2" color="text.secondary" align="center">
					{t('discover.addPlanHintCard.description')}
				</Typography>
			</Box>
		</Card>
	);
};

const DiscoverView = () => {
	useAuthEffects();
	const location = useLocation();
	const navigate = useNavigate();
	const { handleAddFavouriteBtnClick, handleRemoveFavouriteBtnClick, addToWishlistPlanInfo, setAddToWishlistPlanInfo, addFavouriteError, resetAddFavouriteError } = useWishlistLogic();
	const { favouriteIds } = useFavourites();
	const [addPlanHintDialogOpen, setAddPlanHintDialogOpen] = useState(false);
	const { data: globalTags } = useFindGlobalTagsQuery({ excludeUnused: true });
	const { t } = useTranslation();
	const [showLoader, setShowLoader] = useState(true);
	const theme = useTheme();
	const globalTagsSet = useMemo(() => new Set(globalTags?.map((tag) => tag.name)), [globalTags]);
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
	const isXsUp = useMediaQuery(theme.breakpoints.up('sm'));
	const { selectedRegion } = useShippingRegionContext();
	// Parse selected tags from URL query parameters
	const selectedTags = useMemo(() => {
		const urlParams = new URLSearchParams(location.search);
		const tagsParam = urlParams.get('tags');
		if (tagsParam) {
			return tagsParam.split(',').filter(tag => tag.trim() !== '' && globalTagsSet.has(tag));
		}
		return [];
	}, [location.search]);
	
	const selectedTagsSet = useMemo(() => new Set(selectedTags), [selectedTags]);
	const { data: listItems, isLoading } = usePlansListPublicQuery({ tags: selectedTags, countryCode: selectedRegion as string }, {skip: !selectedRegion});

	useEffect(() => {
		if (!isLoading) {
			setShowLoader(false);
		}
	}, [isLoading]);

	const handleTagClick = (tagName: string) => {
		const urlParams = new URLSearchParams(location.search);
		let newSelectedTags: string[];

		if (selectedTagsSet.has(tagName)) {
			// Remove tag
			newSelectedTags = selectedTags.filter((tag) => tag !== tagName);
		} else {
			// Add tag
			ReactGA.event({
				category: 'UI',
				action: 'TagSelected',
				label: tagName,
			});
			newSelectedTags = [...selectedTags, tagName];
		}

		// Update URL with new tags
		if (newSelectedTags.length > 0) {
			urlParams.set('tags', newSelectedTags.join(','));
		} else {
			urlParams.delete('tags');
		}

		const newSearch = urlParams.toString();
		const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
		navigate(newPath, { replace: true });
	};

	return <PageWrapper isLoading={showLoader}>
		<Grid2 container spacing={4} mt={4}>
			<Grid2 size={{xs: 10, md: 6}} offset={{xs: 1, md: 4}} justifyItems='end'>
				<Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'flex-end', flexDirection: 'column', width: 'max-content'}} gap={2}>
					<Typography variant='h4' sx={{fontWeight: 600}}>{t('discover.selectCategories')}</Typography>
					<Box mr={8}>
						<img src={theme.palette.mode === 'dark' ? arrowRightImageDark : arrowRightImage} alt="Discover" height={64} />
					</Box>
				</Box>
			</Grid2>
			<Grid2 size={{xs: 12, md: 10}} offset={{xs: 0, md: 1}}>
				{globalTags && <Box display='flex' flexWrap='wrap' gap={1} justifyContent='center'>
					{globalTags?.map((tag) => <Chip sx={isMdUp ? {fontSize: '16px', padding: theme.spacing(2.5, 2), borderRadius: 8} : {fontSize: '14px', padding: theme.spacing(2, 1), borderRadius: 8}} key={tag.name} clickable onClick={() => handleTagClick(tag.name)} label={t(`tags.${tag.name}`, {defaultValue: `${tag.name[0].toUpperCase()}${tag.name.slice(1)}`})} color={selectedTagsSet.has(tag.name) ? 'primary' : 'default'} />)}
				</Box>}
			</Grid2>
			<Grid2 size={{xs: 10, md: 10}} offset={{xs: 1, md: 2}}>
				<Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flexDirection: 'column', width: 'max-content'}} gap={2}>
					<Typography variant='h5' sx={{fontWeight: 600}}>{t('discover.buyAndBuild')}</Typography>
					<Box ml={5}><img src={theme.palette.mode === 'dark' ? arrowLeftImageDark : arrowLeftImage} alt="Discover" height={64} /></Box>
				</Box>
			</Grid2>
		</Grid2>
		<Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
			<RegionSelector />
		</Box>
		<Divider sx={{mt: 1}}/>
		<ImageList gap={8} cols={isMdUp ? 3 : isXsUp ? 2 : 1} rowHeight={400}>
			{listItems?.map((item, idx) => <PlanListItemEl
				key={`${item.id}-${idx}`}
				item={item}
				isFavourited={favouriteIds.has(item.id)}
				onAddFavouriteBtnClick={() => handleAddFavouriteBtnClick(item)}
				onRemoveFavouriteBtnClick={() => handleRemoveFavouriteBtnClick(item.id)}
			/>)}
			<PlanListItemContainer>
				<AddPlanHintCard onCreate={() => setAddPlanHintDialogOpen(true)} />
			</PlanListItemContainer>
		</ImageList>
		<SuggestionDialog 
			open={addPlanHintDialogOpen}
			onClose={() => setAddPlanHintDialogOpen(false)}
		/>
		<AddToWishlistDialog 
			open={!!addToWishlistPlanInfo}
			onClose={() => setAddToWishlistPlanInfo(null)}
			plan={addToWishlistPlanInfo}
		/>
		<Snackbar open={Boolean(addFavouriteError)} autoHideDuration={6000} onClose={resetAddFavouriteError}>
			<Alert severity='error'>{(addFavouriteError as any)?.data?.message}</Alert>
		</Snackbar>
	</PageWrapper>;
};

export default DiscoverView;
